/**
 * ResultsScene - Displays level completion or game over results
 *
 * Features:
 * - "Level Complete" (green) or "Game Over" (red) header
 * - Score breakdown card with kills, gate bonuses, army bonus
 * - Final score total in gold
 * - Navigation buttons (Next Level, Retry, Menu)
 * - Interstitial ad placeholder every 5 completed levels (FR-022)
 * - New high score indicator
 *
 * Per PRD section 5.2 and Task #20 specifications
 */

import Phaser from 'phaser';
import {
  COLORS,
  COLOR_CSS,
  GAME_WIDTH,
  GAME_HEIGHT,
  FONTS,
  TEXT_SIZES,
  SPACING,
  DURATIONS,
  SAFE_AREA,
} from '../config';
import { Button } from '../ui/Button';
import { saveSystem } from '../systems/SaveSystem';
import { audioSystem } from '../systems/AudioSystem';
import { adSystem } from '../systems/AdSystem';
import { logSceneEvent } from '../utils/automation';
import { createCommandDeckBackground, createSceneHeader } from '../ui/SceneChrome';
import { WeaponType } from '../utils/constants';
import type { RunGrade } from '../systems/RunPerformance';
import { getWeaponDisplayName } from '../ui/weaponVisuals';

/**
 * Data passed to ResultsScene when starting it
 */
export interface ResultsSceneData {
  /** Level that was just played */
  level: number;
  /** Current difficulty tier */
  tier: number;
  /** True if player completed the level, false if game over */
  isVictory: boolean;
  /** Optional failure reason for analytics and debugging */
  failReason?: 'army' | 'base';
  /** Base HP remaining when the run ends */
  baseHpRemaining?: number;
  /** Number of leak events taken during the run */
  leaks?: number;
  baseHpMax?: number;
  enemyKillCount?: number;
  shotsFired?: number;
  shotsHit?: number;
  accuracy?: number;
  grade?: RunGrade;
  weapon?: WeaponType;
  soldiersRemaining?: number;
  finaleDefeated?: boolean;
  isNewHighScore?: boolean;
  /** Score breakdown from the completed level */
  breakdown: {
    kills: number;
    gateBonus: number;
    armyBonus: number;
    total: number;
  };
}

export class ResultsScene extends Phaser.Scene {
  private levelData!: ResultsSceneData;
  private isNewHighScore: boolean = false;

  constructor() {
    super({ key: 'ResultsScene' });
  }

  init(data: ResultsSceneData): void {
    this.levelData = data;
    this.isNewHighScore = Boolean(data.isNewHighScore);

    // ScoreSystem has already compared and saved the score before this scene
    // starts. Saving it again here suppressed the new-high-score indicator.
    if (this.levelData.isVictory) {
      // Unlock next level
      if (this.levelData.level < 10) {
        saveSystem.unlockLevel(this.levelData.level + 1, this.levelData.tier);
      }

      // Update stats
      saveSystem.updateStats({
        totalLevelsCompleted: 1,
      });

      // Check for interstitial ad (every 5 levels)
      this.checkInterstitialAd();
    }
  }

  create(): void {
    logSceneEvent('ResultsScene');
    // If this was level 10 victory, redirect to VictoryScene for special celebration
    if (this.levelData.isVictory && this.levelData.level >= 10) {
      this.scene.start('VictoryScene', {
        tier: this.levelData.tier,
        breakdown: this.levelData.breakdown,
      });
      return;
    }

    // Fade in
    this.cameras.main.fadeIn(DURATIONS.screenTransition, 0, 0, 0);

    // Play appropriate music sting (stops gameplay music)
    audioSystem.playMusic(this.levelData.isVictory ? 'victory' : 'gameover');

    this.createBackground();
    this.createHeader();
    this.createScoreCard();
    this.createButtons();
  }

  // ===========================================================================
  // CREATION
  // ===========================================================================

  /**
   * Creates the background
   */
  private createBackground(): void {
    createCommandDeckBackground(this, this.levelData.isVictory ? COLORS.player : COLORS.enemy);
  }

  /**
   * Creates the header text (Level Complete or Game Over)
   */
  private createHeader(): void {
    const headerY = SAFE_AREA.top + SPACING.space7;

    const headerText = this.levelData.isVictory ? 'Breach Cleared' : 'Squad Lost';
    const headerColor = this.levelData.isVictory ? COLORS.player : COLORS.enemy;
    createSceneHeader(this, {
      title: headerText,
      kicker: this.levelData.isVictory ? 'Mission outcome // success' : 'Mission outcome // failed',
      accent: headerColor,
      y: headerY,
    });

    // Level indicator
    const levelText = `Level ${this.levelData.level}`;
    const tierText = this.levelData.tier > 1 ? ` • Tier ${this.levelData.tier}` : '';

    this.add.text(
      GAME_WIDTH / 2,
      headerY + 48,
      levelText + tierText,
      {
        fontFamily: FONTS.body,
        fontSize: `${TEXT_SIZES.title}px`,
        color: COLOR_CSS.uiTextSecondary,
      }
    ).setOrigin(0.5);

    if (!this.levelData.isVictory) {
      const reason = this.levelData.failReason === 'base'
        ? 'Shield collapsed — intercept enemies before they breach.'
        : 'Squad depleted — dodge contact and rebuild at gates.';
      this.add.text(GAME_WIDTH / 2, headerY + 78, reason, {
        fontFamily: FONTS.body,
        fontSize: '14px',
        color: COLOR_CSS.uiTextSecondary,
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 72 },
      }).setOrigin(0.5);
    }
  }

  /**
   * Creates the score breakdown card
   */
  private createScoreCard(): void {
    const cardY = GAME_HEIGHT * 0.335;
    const cardWidth = GAME_WIDTH - SPACING.space6 * 2;
    const cardHeight = 274;

    // Card background
    const card = this.add.graphics();
    card.fillStyle(COLORS.bgOverlay, 0.6);
    card.fillRoundedRect(
      (GAME_WIDTH - cardWidth) / 2,
      cardY,
      cardWidth,
      cardHeight,
      16
    );

    const rowStartY = cardY + 38;
    const rowHeight = 31;
    const labelX = GAME_WIDTH / 2 - 80;
    const valueX = GAME_WIDTH / 2 + 80;

    const rowStyle = {
      fontFamily: FONTS.body,
      fontSize: '15px',
      color: COLOR_CSS.uiText,
    };

    const valueStyle = {
      fontFamily: FONTS.numbers,
      fontSize: '15px',
      color: COLOR_CSS.gold,
    };

    const grade = this.levelData.grade ?? (this.levelData.isVictory ? 'B' : '-');
    const weapon = this.levelData.weapon ?? WeaponType.Pistol;
    this.add.text(GAME_WIDTH / 2, cardY + 14, `GRADE ${grade}  •  ${getWeaponDisplayName(weapon).toUpperCase()}`, {
      fontFamily: FONTS.display,
      fontSize: '17px',
      color: grade === 'S' ? COLOR_CSS.gold : COLOR_CSS.uiText,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const accuracy = this.levelData.accuracy ?? (
      (this.levelData.shotsFired ?? 0) > 0
        ? (this.levelData.shotsHit ?? 0) / (this.levelData.shotsFired ?? 1)
        : 0
    );
    const rows: Array<[string, string]> = [
      ['Enemies Eliminated', String(this.levelData.enemyKillCount ?? 0)],
      ['Accuracy', `${(accuracy * 100).toFixed(1)}%`],
      ['Shield', `${this.levelData.baseHpRemaining ?? 0} / ${this.levelData.baseHpMax ?? 0}`],
      ['Breaches', String(this.levelData.leaks ?? 0)],
      ['Final Squad', String(this.levelData.soldiersRemaining ?? 0)],
    ];

    rows.forEach(([label, value], index) => {
      const y = rowStartY + rowHeight * index;
      this.add.text(labelX, y, label, rowStyle).setOrigin(0, 0.5);
      this.add.text(valueX, y, value, valueStyle).setOrigin(1, 0.5);
    });

    // Divider line
    const dividerY = cardY + cardHeight - 58;
    const divider = this.add.graphics();
    divider.lineStyle(1, COLORS.uiTextSecondary, 0.3);
    divider.lineBetween(
      (GAME_WIDTH - cardWidth) / 2 + 20,
      dividerY,
      (GAME_WIDTH + cardWidth) / 2 - 20,
      dividerY
    );

    // Total score
    const totalY = cardY + cardHeight - 27;
    this.add.text(
      labelX,
      totalY,
      'TOTAL',
      {
        fontFamily: FONTS.display,
        fontSize: `${TEXT_SIZES.title}px`,
        color: COLOR_CSS.uiText,
        fontStyle: 'bold',
      }
    ).setOrigin(0, 0.5);

    const totalScore = this.add.text(
      valueX,
      totalY,
      String(this.levelData.breakdown.total),
      {
        fontFamily: FONTS.numbers,
        fontSize: `${TEXT_SIZES.headline}px`,
        color: COLOR_CSS.gold,
        fontStyle: 'bold',
      }
    ).setOrigin(1, 0.5);

    // Animate total score counting up
    this.animateScoreCount(totalScore, this.levelData.breakdown.total);

    // New high score indicator
    if (this.isNewHighScore) {
      const highScoreText = this.add.text(
        GAME_WIDTH / 2,
        cardY + cardHeight + 20,
        '★ NEW HIGH SCORE! ★',
        {
          fontFamily: FONTS.display,
          fontSize: `${TEXT_SIZES.body}px`,
          color: COLOR_CSS.gold,
          fontStyle: 'bold',
        }
      ).setOrigin(0.5);

      // Pulse animation
      this.tweens.add({
        targets: highScoreText,
        scale: 1.1,
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Animates the score counting up
   */
  private animateScoreCount(textObj: Phaser.GameObjects.Text, targetScore: number): void {
    this.tweens.addCounter({
      from: 0,
      to: targetScore,
      duration: 1000,
      ease: 'Cubic.easeOut',
      onUpdate: (tween) => {
        const rawValue = tween.getValue();
        const value = Math.floor(rawValue ?? 0);
        textObj.setText(String(value));
      },
    });
  }

  /**
   * Creates the navigation buttons
   */
  private createButtons(): void {
    const buttonWidth = 220;
    const buttonHeight = 56;
    const buttonSpacing = SPACING.space3;
    const startY = GAME_HEIGHT * 0.77;

    if (this.levelData.isVictory) {
      // Victory buttons: Next Level, Retry, Menu
      // Note: Level 10 victory redirects to VictoryScene, so this is always < level 10

      // Next Level button
      new Button(this, {
        x: GAME_WIDTH / 2,
        y: startY,
        text: 'Next Level',
        variant: 'primary',
        width: buttonWidth,
        height: buttonHeight,
        onClick: () => this.goToNextLevel(),
      });

      // Retry button
      new Button(this, {
        x: GAME_WIDTH / 2,
        y: startY + buttonHeight + buttonSpacing,
        text: 'Retry',
        variant: 'secondary',
        width: buttonWidth,
        height: 48,
        onClick: () => this.retryLevel(),
      });

      // Menu button
      new Button(this, {
        x: GAME_WIDTH / 2,
        y: startY + (buttonHeight + buttonSpacing) * 2 - 8,
        text: 'Main Menu',
        variant: 'secondary',
        width: buttonWidth,
        height: 48,
        onClick: () => this.goToMenu(),
      });
    } else {
      // Game Over buttons: Try Again, Menu

      // Try Again button
      new Button(this, {
        x: GAME_WIDTH / 2,
        y: startY,
        text: 'Try Again',
        variant: 'primary',
        width: buttonWidth,
        height: buttonHeight,
        onClick: () => this.retryLevel(),
      });

      // Menu button
      new Button(this, {
        x: GAME_WIDTH / 2,
        y: startY + buttonHeight + buttonSpacing,
        text: 'Main Menu',
        variant: 'secondary',
        width: buttonWidth,
        height: 48,
        onClick: () => this.goToMenu(),
      });
    }
  }

  // ===========================================================================
  // NAVIGATION
  // ===========================================================================

  /**
   * Goes to the next level
   */
  private goToNextLevel(): void {
    audioSystem.play('button_tap');
    this.transitionTo('GameScene', {
      level: this.levelData.level + 1,
      tier: this.levelData.tier,
    });
  }

  /**
   * Retries the current level
   */
  private retryLevel(): void {
    audioSystem.play('button_tap');
    this.transitionTo('GameScene', {
      level: this.levelData.level,
      tier: this.levelData.tier,
    });
  }

  /**
   * Returns to the main menu
   */
  private goToMenu(): void {
    audioSystem.play('button_tap');
    this.transitionTo('TitleScene');
  }

  /**
   * Handles scene transition with fade
   */
  private transitionTo(sceneKey: string, data?: object): void {
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(sceneKey, data);
    });
  }

  // ===========================================================================
  // ADS
  // ===========================================================================

  /**
   * Checks if an interstitial ad should be shown
   * Per FR-022: Show interstitial every 5 completed levels
   * Uses AdSystem which handles the timing internally
   */
  private checkInterstitialAd(): void {
    // AdSystem tracks level completions and shows ads every 5 levels
    adSystem.onLevelComplete();
  }

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  /**
   * Placeholder for sound effects (implemented in Task #28)
   */
}
